import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';

export default function RewardsConfig() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const [pointsCap, setPointsCap] = useState(100);
  const [pointsPerShare, setPointsPerShare] = useState(5);
  const [pointsPerReferral, setPointsPerReferral] = useState(10);
  const [referralsNeededForReward, setReferralsNeededForReward] = useState(10);
  const [rewardsDefaultPointsPerDollar, setRewardsDefaultPointsPerDollar] = useState(1);
  const [rewardType10PercentOffEnabled, setRewardType10PercentOffEnabled] = useState(true);
  const [rewardTypeFreeDrinkEnabled, setRewardTypeFreeDrinkEnabled] = useState(true);
  const [rewardTypeFreeSideEnabled, setRewardTypeFreeSideEnabled] = useState(true);
  const [rewardTypeCustomPercentOff, setRewardTypeCustomPercentOff] = useState('');
  const [rewardTypeFreeMenuItemId, setRewardTypeFreeMenuItemId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configRes, menuRes, statsRes] = await Promise.all([
        api('/api/owners/me/rewards-config', { getToken }),
        api('/api/owners/me/menu', { getToken }),
        api('/api/owners/me/rewards/stats', { getToken }),
      ]);
      if (!configRes.ok) {
        if (configRes.status === 403) {
          navigate('/', { replace: true });
          return;
        }
        throw new Error('Failed to load rewards config');
      }
      const configData = await configRes.json();
      setPointsCap(configData.pointsCap ?? 100);
      setPointsPerShare(configData.pointsPerShare ?? 5);
      setPointsPerReferral(configData.pointsPerReferral ?? 10);
      setReferralsNeededForReward(configData.referralsNeededForReward ?? 10);
      setRewardsDefaultPointsPerDollar(configData.rewardsDefaultPointsPerDollar ?? 1);
      setRewardType10PercentOffEnabled(configData.rewardType10PercentOffEnabled ?? true);
      setRewardTypeFreeDrinkEnabled(configData.rewardTypeFreeDrinkEnabled ?? true);
      setRewardTypeFreeSideEnabled(configData.rewardTypeFreeSideEnabled ?? true);
      setRewardTypeCustomPercentOff(configData.rewardTypeCustomPercentOff ?? '');
      setRewardTypeFreeMenuItemId(configData.rewardTypeFreeMenuItemId ?? '');

      if (menuRes.ok) {
        const menuData = await menuRes.json();
        const items = (menuData.categories || []).flatMap((c) =>
          (c.items || []).map((i) => ({ ...i, categoryName: c.name }))
        );
        setMenuItems(items);
      }
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const body = {
        pointsCap,
        pointsPerShare,
        pointsPerReferral,
        referralsNeededForReward,
        rewardsDefaultPointsPerDollar: Number(rewardsDefaultPointsPerDollar) || 1,
        rewardType10PercentOffEnabled,
        rewardTypeFreeDrinkEnabled,
        rewardTypeFreeSideEnabled,
        rewardTypeCustomPercentOff: rewardTypeCustomPercentOff === '' ? null : Number(rewardTypeCustomPercentOff),
        rewardTypeFreeMenuItemId: rewardTypeFreeMenuItemId === '' ? null : Number(rewardTypeFreeMenuItemId),
      };
      const res = await api('/api/owners/me/rewards-config', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-600 dark:text-slate-400">Loading rewards...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Rewards</h1>

      {stats && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Share Stats</h2>
          <p className="text-slate-600 dark:text-slate-400">
            Your menu has been shared <strong>{stats.totalShares}</strong> times.{' '}
            <strong>{stats.totalReferralVisits ?? 0}</strong> people visited shared links.{' '}
            <strong>{stats.totalSuccessfulReferrals}</strong> customers ordered from shared links.
          </p>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 space-y-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Points Settings</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label>
            <span className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Points cap</span>
            <input
              type="number"
              min={1}
              max={10000}
              value={pointsCap}
              onChange={(e) => setPointsCap(Number(e.target.value) || 100)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </label>
          <label>
            <span className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Points per share</span>
            <input
              type="number"
              min={0}
              value={pointsPerShare}
              onChange={(e) => setPointsPerShare(Number(e.target.value) || 0)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </label>
          <label>
            <span className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Points per referral</span>
            <input
              type="number"
              min={0}
              value={pointsPerReferral}
              onChange={(e) => setPointsPerReferral(Number(e.target.value) || 0)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </label>
          <label>
            <span className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Referrals needed for reward</span>
            <input
              type="number"
              min={0}
              value={referralsNeededForReward}
              onChange={(e) => setReferralsNeededForReward(Number(e.target.value) || 0)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </label>
          <label>
            <span className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Default points per $1</span>
            <input
              type="number"
              min={0}
              max={100}
              value={rewardsDefaultPointsPerDollar}
              onChange={(e) => setRewardsDefaultPointsPerDollar(Number(e.target.value) || 1)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 space-y-6">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Reward Types</h2>
        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rewardType10PercentOffEnabled}
              onChange={(e) => setRewardType10PercentOffEnabled(e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-slate-900 dark:text-white">10% off next meal</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rewardTypeFreeDrinkEnabled}
              onChange={(e) => setRewardTypeFreeDrinkEnabled(e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-slate-900 dark:text-white">Free drink</span>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={rewardTypeFreeSideEnabled}
              onChange={(e) => setRewardTypeFreeSideEnabled(e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-slate-900 dark:text-white">Free side</span>
          </label>
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-2">Custom % off</label>
            <input
              type="number"
              min={0}
              max={100}
              placeholder="e.g. 15"
              value={rewardTypeCustomPercentOff}
              onChange={(e) => setRewardTypeCustomPercentOff(e.target.value)}
              className="w-full max-w-[120px] px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-2">Free menu item</label>
            <select
              value={rewardTypeFreeMenuItemId}
              onChange={(e) => setRewardTypeFreeMenuItemId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              <option value="">Select item</option>
              {menuItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} {item.categoryName ? `(${item.categoryName})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="w-full sm:w-auto px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-semibold"
      >
        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save'}
      </button>
    </div>
  );
}
